// Drag a table row by a handle: the row follows the pointer and the rows it passes
// slide aside, so where it will land is visible before letting go. Pointer events, so
// a finger works as well as a mouse.
import {
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";

type Drag = { id: string; to: number; dy: number; height: number };

export const useRowDrag = (
  ids: string[],
  onDrop: (id: string, to: number) => void,
  disabled = false,
) => {
  const [drag, setDrag] = useState<Drag | null>(null);
  // Row midpoints at rest, taken once when the drag starts: the rows that slide aside
  // must not move the targets they are measured against.
  const origin = useRef<{ y: number; mids: number[] } | null>(null);

  const end = () => {
    origin.current = null;
    setDrag(null);
  };

  const handleProps = (id: string) => ({
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
      if (disabled || e.button !== 0) return;
      const row = e.currentTarget.closest("tr");
      const body = row?.parentElement;
      if (!row || !body) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      // Page coordinates, so scrolling mid-drag keeps the row under the pointer.
      origin.current = {
        y: e.clientY + window.scrollY,
        mids: Array.from(body.children).map((r) => {
          const box = r.getBoundingClientRect();
          return box.top + window.scrollY + box.height / 2;
        }),
      };
      const from = ids.indexOf(id);
      setDrag({
        id,
        to: from,
        dy: 0,
        height: row.getBoundingClientRect().height,
      });
    },
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
      if (!drag || drag.id !== id || !origin.current) return;
      const { mids, y } = origin.current;
      const from = ids.indexOf(id);
      const moved = e.clientY + window.scrollY - y;
      const to = mids.filter(
        (mid, i) => i !== from && mid < mids[from] + moved,
      ).length;
      // Drawn no further than the first and last rows: past them the table's scroll
      // box would clip the row.
      const dy = Math.max(
        mids[0] - mids[from],
        Math.min(mids[mids.length - 1] - mids[from], moved),
      );
      setDrag({ ...drag, dy, to });
    },
    onPointerUp: () => {
      if (drag && drag.id === id && drag.to !== ids.indexOf(id))
        onDrop(id, drag.to);
      end();
    },
    onPointerCancel: end,
  });

  const rowStyle = (i: number): CSSProperties | undefined => {
    if (!drag) return undefined;
    // By id, not a stored index: a refetch can reorder the rows under a drag.
    const from = ids.indexOf(drag.id);
    if (i === from) return { transform: `translateY(${drag.dy}px)` };
    if (from < drag.to && i > from && i <= drag.to) {
      return { transform: `translateY(${-drag.height}px)` };
    }
    if (drag.to < from && i >= drag.to && i < from) {
      return { transform: `translateY(${drag.height}px)` };
    }
    return undefined;
  };

  return { draggingId: drag?.id ?? null, handleProps, rowStyle };
};
